import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  UserCheck,
  UserX,
  UserMinus,
  Trash2,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

export type VoteFilterType =
  | "all"
  | "verified"
  | "unverified"
  | "anonymous"
  | "anonymized";

interface PollVoteStatsProps {
  totalVotes: number;
  verifiedVotes: number;
  unverifiedVotes: number;
  anonymousVotes: number;
  anonymizedVotes: number;
  currentFilter: VoteFilterType;
  onFilterChange: (filter: VoteFilterType) => void;
  className?: string;
  isLoading?: boolean;
  error?: string | null;
}

const PollVoteStats: React.FC<PollVoteStatsProps> = ({
  totalVotes,
  verifiedVotes,
  unverifiedVotes,
  anonymousVotes,
  anonymizedVotes,
  currentFilter,
  onFilterChange,
  className,
  isLoading = false,
  error = null,
}) => {
  // Calculate percentages
  const calculatePercentage = (votes: number) => {
    return totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
  };

  const verifiedPercentage = calculatePercentage(verifiedVotes);
  const unverifiedPercentage = calculatePercentage(unverifiedVotes);
  const anonymousPercentage = calculatePercentage(anonymousVotes);
  const anonymizedPercentage = calculatePercentage(anonymizedVotes);

  if (error) {
    return (
      <Badge
        variant="outline"
        className={cn(
          "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400",
          className
        )}
      >
        <AlertCircle className="h-3 w-3 mr-1" />
        Error loading vote statistics
      </Badge>
    );
  }

  if (isLoading) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 text-sm text-muted-foreground",
          className
        )}
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading vote statistics...
      </div>
    );
  }

  const hasVotes = totalVotes > 0;

  return (
    <div className={cn("space-y-2", className)}>
      {hasVotes && (
        <div className="flex flex-wrap items-center gap-1">
          <Button
            variant={currentFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => onFilterChange("all")}
            className="h-7 text-xs"
            data-vote-stat="all"
          >
            <Users className="h-3 w-3 mr-1" />
            All ({totalVotes}, {100}%)
          </Button>

          {verifiedVotes > 0 && (
            <Button
              variant={currentFilter === "verified" ? "default" : "outline"}
              size="sm"
              onClick={() => onFilterChange("verified")}
              className="h-7 text-xs vote-stat-verified"
              data-vote-stat="verified"
            >
              <UserCheck className="h-3 w-3 mr-1" />
              Verified ({verifiedVotes}, {verifiedPercentage}%)
            </Button>
          )}

          {unverifiedVotes > 0 && (
            <Button
              variant={currentFilter === "unverified" ? "default" : "outline"}
              size="sm"
              onClick={() => onFilterChange("unverified")}
              className="h-7 text-xs vote-stat-unverified"
              data-vote-stat="unverified"
            >
              <UserX className="h-3 w-3 mr-1" />
              Unverified ({unverifiedVotes}, {unverifiedPercentage}%)
            </Button>
          )}

          {anonymousVotes > 0 && (
            <Button
              variant={currentFilter === "anonymous" ? "default" : "outline"}
              size="sm"
              onClick={() => onFilterChange("anonymous")}
              className="h-7 text-xs vote-stat-anonymous"
              data-vote-stat="anonymous"
            >
              <UserMinus className="h-3 w-3 mr-1" />
              Anonymous ({anonymousVotes}, {anonymousPercentage}%)
            </Button>
          )}

          {anonymizedVotes > 0 && (
            <Button
              variant={currentFilter === "anonymized" ? "default" : "outline"}
              size="sm"
              onClick={() => onFilterChange("anonymized")}
              className="h-7 text-xs vote-stat-anonymized"
              data-vote-stat="anonymized"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Anonymized ({anonymizedVotes}, {anonymizedPercentage}%)
              <Badge variant="secondary" className="ml-1 text-xs">
                GDPR
              </Badge>
            </Button>
          )}
        </div>
      )}

      {hasVotes && (
        <p className="text-xs text-muted-foreground">
          Click on a poll option below to vote. Filter results by clicking the
          vote types above.
        </p>
      )}
    </div>
  );
};

export default PollVoteStats;
